// Import hooks.
import {
	useInputForm,
	UseInputFormResult,
} from '@app/hooks/view/input';
// Import app models.
import {
	ValidatorResponse,
} from '@app/models/app';
// Import props.
import {
	InputFormProps,
	InputYesNoProps,
} from '@app/props';

interface UseInputYesNoParameters {
	id: string;
	label: string;
	required: boolean;
	disabled: boolean;
	infoLabel?: string,
	yesLabel?: string,
	noLabel?: string
	isYesChecked?: boolean;
	showValidation?: boolean;
}

interface UseInputYesNoResults extends UseInputFormResult<boolean | undefined, InputYesNoProps> {
	getSelectedValueDisplayName: () => string | undefined;
}

const useInputYesNo = (params: UseInputYesNoParameters): UseInputYesNoResults => {

	// #region exported methods

	/**
	 * Returns props for form input. 
	 **/
	const applyYesNoProps = (): InputYesNoProps => {
		// First apply the form input props.
		let formProps: InputFormProps = applyProps();
		// Then apply the yes/no props.
		return {
			...formProps,
			infoLabel: params.infoLabel,
			isYes: other.value,
			yesLabel: params.yesLabel !== undefined
				? params.yesLabel
				: 'Yes',
			noLabel: params.noLabel !== undefined
				? params.noLabel
				: 'No',
			onChange: other.setInput,
		}
	};

	/**
	 * Returns the display name version of the selected value.
	 */
	const getSelectedValueDisplayName = (): string | undefined => {
		let selectedValueDisplayName: string | undefined = undefined;

		let isYesSelected: boolean | undefined = other.value;
		if (isYesSelected !== undefined) {
			selectedValueDisplayName = isYesSelected ? 'Yes' : 'No';
		}
		return selectedValueDisplayName;
	}

	// #endregion exported methods


	// #region private methods

	/**
	 * Validates the yes/no answer.
	 * @param state
	 */
	const validateYesNoAnswer = (isYesChecked: boolean | undefined, required: boolean): ValidatorResponse => {
		if (required
			&& isYesChecked === undefined) {
			return new ValidatorResponse(false, 'An answer is required.');
		}

		return new ValidatorResponse(true, String.empty());
	}

	// #endregion private methods


	// #region variables

	// Instantiate the 'address' input.
	const { applyProps, ...other } = useInputForm<boolean | undefined>({
		id: params.id,
		label: params.label,
		required: params.required,
		disabled: params.disabled,
		initialInput: params.isYesChecked,
		showValidation: params.showValidation,
		validators: [validateYesNoAnswer],
	});

	// #endregion variables


	return {
		applyProps: applyYesNoProps,
		getSelectedValueDisplayName: getSelectedValueDisplayName,
		...other,
	};
}

// Export parameters.
export {
	UseInputYesNoParameters,
}

// Export results.
export {
	UseInputYesNoResults,
}

export default useInputYesNo;